/**
 * ClientSelector Component
 *
 * Shared client selection dropdown for admin pages. Integrates with AdminContext
 * to provide a consistent client selection experience across all admin pages.
 *
 * Features:
 * - Single-select Autocomplete with search functionality
 * - Loading, error, and disabled states
 * - Purple theme styling (#7C3AED) for consistency
 * - 40px height, 2px border as per design specs
 * - Full accessibility support (ARIA labels, keyboard navigation)
 * - Client name as primary text, client ID as secondary text
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';

/**
 * ClientSelector Component
 *
 * @param {Object} props
 * @param {string|null} props.value - Currently selected client ID
 * @param {function} props.onChange - Callback when client selection changes (receives clientId string or null)
 * @param {Array} props.clients - Array of client objects with clientId and clientName
 * @param {boolean} props.loading - Loading state for client data
 * @param {Error|null} props.error - Error object if client fetch failed
 * @param {boolean} props.disabled - Whether the selector is disabled
 */
export default function ClientSelector({
  value,
  onChange,
  clients = [],
  loading = false,
  error = null,
  disabled = false,
}) {
  // Find the currently selected client object
  const selectedClient = useMemo(() => {
    return value ? clients.find((c) => c.clientId === value) : null;
  }, [value, clients]);

  // Handle selection change
  const handleChange = (event, newValue) => {
    onChange(newValue?.clientId || null);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || 'Failed to load clients'}
        </Alert>
      )}

      {/* Autocomplete Component */}
      <Autocomplete
        value={selectedClient}
        onChange={handleChange}
        options={clients}
        getOptionLabel={(option) => option.clientName || option.clientId || ''}
        loading={loading}
        disabled={disabled || loading}
        size="small"
        isOptionEqualToValue={(option, value) =>
          option.clientId === value?.clientId
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select a client..."
            variant="outlined"
            size="small"
            inputProps={{
              ...params.inputProps,
              'aria-label': 'Select client',
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.clientId}>
            <Box>
              <Typography variant="body1">
                {option.clientName || option.clientId}
              </Typography>
              {option.clientName && option.clientId && (
                <Typography variant="caption" color="text.secondary">
                  {option.clientId}
                </Typography>
              )}
            </Box>
          </li>
        )}
        noOptionsText="No clients found"
        sx={{
          minWidth: 250,
          '& .MuiOutlinedInput-root': {
            height: '40px',
            '& fieldset': {
              borderColor: '#7C3AED', // Purple color
              borderWidth: 2,
            },
            '&:hover fieldset': {
              borderColor: '#6D28D9', // Darker purple on hover
            },
            '&.Mui-focused fieldset': {
              borderColor: '#7C3AED',
            },
            '&.Mui-disabled fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.26)',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#7C3AED',
          },
          '& .MuiSvgIcon-root': {
            color: '#7C3AED',
          },
        }}
      />

      {/* Empty State Message */}
      {!loading && clients.length === 0 && !error && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          No clients available
        </Typography>
      )}
    </Box>
  );
}

ClientSelector.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      clientId: PropTypes.string.isRequired,
      clientName: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  error: PropTypes.instanceOf(Error),
  disabled: PropTypes.bool,
};
